import {authenticate, postUpload, runWorkflow, getWorkflow} from "../api";
import {bytesToMegaBytes} from "src/util/util";
import {v4 as uuidv4} from "uuid";
import {isDefined} from "../api/helpers";

const actions = {
  async authenticateAsync({commit}) {
    const auth = await authenticate();
    const noConfig = Object.assign({}, auth);
    if (noConfig.config) delete noConfig.config;
    commit("auth/setAuth", noConfig);
    commit("data/setConfig", auth && auth.config);
  },

  async runWorkflows({commit}, {uploadId, uploadName, workflows, dateAdded}) {
    await doRunWorkflows(commit, uploadId, uploadName, workflows, dateAdded);
  },

  async uploadUpload(
    {commit},
    {
      id,
      name,
      language,
      selectedCase,
      minSpeakerCount,
      maxSpeakerCount,
      storagePath,
      file,
      lastModifiedDate,
      dateAdded,
      workflows,
      notify,
      remark,
      uploadType,
      createTranslation,
    }
  ) {
    const notification = {
      id: dateAdded,
      closeOnClick: true,
      showClose: true,
      persist: true,
      count: 1,
      title: "Uploading Files",
      message: "Please do not refresh the page until completed. You may use a second tab for other tasks.",
    };
    notify.$n(notification);
    const payload = {
      id,
      dateAdded,
      name: name && name.length > 0 ? name : file.name,
      size: bytesToMegaBytes(file.size),
      status: "Queued",
      progress: 0,
      uploadId: null,
    };
    let fakePreparingProgress = null;
    try {
      commit("data/setUploadResponse", payload);
      const uploadProgressCb = (e) => doUploadProgress(e, payload);
      const uploadController = new AbortController();
      const uploadOpts = {
        uploadProgressCb,
        uploadController,
      };
      payload.uploadController = uploadController;
      // fake progress is used for preparing step which occurs after the upload.
      // during preparing, the server is then prepping file and uploading to s3
      let step = 0.3;
      let curr = 0;
      fakePreparingProgress = window.setInterval(() => {
        if (payload.status == "Preparing") {
          // payload.progress = Math.min(99, payload.progress + Math.floor(Math.random() * 2) + 1);
          curr += step;
          payload.progress = Math.round(Math.atan(curr) / (Math.PI / 2) * 100 * 1000) / 1000;
          if (curr >= 4) {
            step = 0.1;
          }
        }
        commit("data/setUploadResponse", payload);
      }, 1000);
      const response = await postUpload(
        name && name.length > 0 ? name : null,
        language && language.length > 0 ? language : null,
        selectedCase && selectedCase.length > 0 ?
          selectedCase :
          null,
        minSpeakerCount,
        maxSpeakerCount,
        storagePath && storagePath.length > 0 ?
          storagePath :
          null,
        file,
        lastModifiedDate,
        uploadOpts,
        remark,
        uploadType
      );
      //Upload stored in server. Trigger an update to the case summary.
      commit("data/incrementCaseSummaryUpdateCount");

      window.clearInterval(fakePreparingProgress);
      payload.status = "Complete";
      payload.progress = 100;
      payload.uploadId = response.id;
      commit("data/setUploadResponse", payload);
      commit("data/setUploadsAdded", payload.uploadId);
      await doRunWorkflows(commit, response.id, payload.name, workflows, dateAdded, createTranslation);
    } catch (ex) {
      if (fakePreparingProgress) {
        window.clearInterval(fakePreparingProgress);
      }
      if (ex?.code == "ERR_CANCELED") {
        payload.status = "Canceled";
      } else {
        payload.status = "Failed";
        console.error("failed to upload uploads", ex);
      }
      payload.progress = 100;
      commit("data/setUploadResponse", payload);
    }
    try {
      notify.$s.decrement(notification.id);
    } catch (ex) {
      console.error("Failed to decrement counter", ex);
    }
  },

  async pollWorkflow({commit}, workflowPayload) {
    if (workflowPayload && workflowPayload.workflowPersistenceId) {
      try {
        const result = await getWorkflow(workflowPayload.workflowPersistenceId);
        if (result.failureReason) throw new Error(result.failureReason);
        if (result.completeTime) {
          console.log(`Workflow: ${workflowPayload.workflowPersistenceId} ${workflowPayload.name} - Completed at: ${result.completeTime}`);
          workflowPayload.status = "Complete";
          workflowPayload.progress = 100;
          commit("data/setWorkflowResponse", workflowPayload);
        } else {
          let curr = workflowPayload.progressCalcVar ?? 0;
          if (curr >= 4) {
            curr += Math.random() * 0.15;
          } else {
            curr += Math.random() * 0.2 + 0.1;
          }
          workflowPayload.progressCalcVar = curr;
          workflowPayload.progress = Math.round(Math.atan(curr) / (Math.PI / 2) * 100 * 1000) / 1000;
          // workflowPayload.progress = Math.min(99, workflowPayload.progress + Math.floor(Math.random() * 9) + 1);
          console.log(`Workflow: ${workflowPayload.workflowPersistenceId} ${workflowPayload.name} - ${workflowPayload.progress}% - Last Checked at: ${new Date()}`);
          commit("data/setWorkflowResponse", workflowPayload);
          return false;
        }
      } catch (ex2) {
        // Mark as failure
        console.error(`Workflow: ${workflowPayload.workflowPersistenceId} ${workflowPayload.name} - Failed to complete`, ex2);
        workflowPayload.status = "Failed";
        workflowPayload.progress = 100;
        commit("data/setWorkflowResponse", workflowPayload);
      };
      return true;
    }
  },
};

const doUploadProgress = (e, item) => {
  if (e.progress > 0 && e.progress < 1) item.status = "Uploading";
  item.progress = parseInt( e.progress * 100 );
  if (e.progress == 1) {
    item.status = "Preparing";
    item.progress = 0;
  }
};

const doRunWorkflows = async (commit, uploadId, uploadName, workflows, dateAdded,
  createTranslation) => {
  console.log("doRunWorkflows createTranslation =", createTranslation);
  if (
    !isDefined(commit) ||
    !isDefined(uploadId) ||
    !isDefined(uploadName) ||
    !isDefined(workflows) ||
    !Array.isArray(workflows) ||
    workflows.length < 1
  ) return;

  // Run workflows if there are any
  const workflowPromises = [];
  for (let i = 0; i < workflows.length; i++) {
    const workflowName = workflows[i];

    // Update the payload to confirm we're working on this workflow
    const workflowPayload = {
      id: uuidv4(),
      uploadId: uploadId,
      uploadName: uploadName,
      dateAdded,
      name: workflowName,
      status: "Requesting",
      progress: 0,
      // workflowId: null,
      workflowId: workflowName,
      workflowPersistenceId: null,
    };
    commit("data/setWorkflowResponse", workflowPayload);

    // Trigger the workflow
    const workflowPromise = runWorkflow(
      workflowName.replace(/\W/g, ""),
      uploadId,
      null, null, null, // parentProcessId, language, additionalNotes
      createTranslation
    ).then((workflowPersistenceId) => {
      // Mark as successful
      console.log(`Workflow: ${workflowName} - now running with ref: ${workflowPersistenceId}`);
      workflowPayload.workflowPersistenceId = workflowPersistenceId;
      workflowPayload.status = "In Progress";
      workflowPayload.progress = Math.floor(Math.random() * 11);
      commit("data/setWorkflowResponse", workflowPayload);
      return Promise.resolve(workflowPersistenceId);
    }).catch((ex2) => {
      // Mark as failure
      console.error(`Workflow: ${workflowName} - Failed to run`, ex2);
      workflowPayload.status = "Failed";
      workflowPayload.progress = 100;
      commit("data/setWorkflowResponse", workflowPayload);
    });

    // Store the promise
    workflowPromises.push(workflowPromise);
  }

  // Await all workflow outcomes
  await Promise.allSettled(workflowPromises);
};

export default actions;
